import React from 'react';
import { Chip } from '@mui/material';
import { DivisionMileStone } from '../../../../../../api/divisionMileStones/types';
import { styles } from './MilestoneAccordion.styles';
import { ChipSize, ChipVariant, TaskChipType } from './types';

interface TaskChipProps {
     task: DivisionMileStone;
     getTaskChipType: (task: DivisionMileStone) => TaskChipType;
     getTaskCheckboxState: (taskId: string) => boolean;
     workflowTasksCount?: number;
     size?: ChipSize;
     variant?: ChipVariant;
     sx?: any;
}

// Constants
const CHIP_LABELS = {
     TASK_UNSAVED: 'unsaved changes',
     SYNC_IN_PROGRESS: 'sync in progress',
     NOT_APPLICABLE: 'not applicable',
     DRAFT: 'saved',
} as const;

// TaskChip component for rendering task-specific chips
export const TaskChip: React.FC<TaskChipProps> = ({
     task,
     getTaskChipType,
     getTaskCheckboxState,
     workflowTasksCount,
     size = 'small',
     variant = 'filled',
     sx
}) => {
     const chipType = getTaskChipType(task);
     const currentCheckboxState = getTaskCheckboxState(task.id);
     const taskHasUnsavedChanges = currentCheckboxState !== undefined &&
          !task.automated &&
          currentCheckboxState !== task.status;

     // Priority 1: Show unsaved changes if detected
     if (taskHasUnsavedChanges) {
          return (
               <Chip
                    label={CHIP_LABELS.TASK_UNSAVED}
                    size={size}
                    color="info"
                    variant={variant}
                    sx={{ ...styles.unsavedChip, ...sx }}
               />
          );
     }

     if (task.mode === 'draft' && !task.status) {
          return (
               <>
                    <Chip
                         label={CHIP_LABELS.DRAFT}
                         size={size}
                         color="warning"
                         variant={variant}
                         sx={{ ...styles.taskChip, ...sx }}
                    />
                    <Chip
                         label={CHIP_LABELS.NOT_APPLICABLE}
                         size={size}
                         color="error"
                         variant={variant}
                         sx={{ ...styles.taskChip, ...sx }}
                    />
               </>
          );
     }
     // Priority 2: Show "Not Applicable" when status is false
     if (!task.status) {
          return (
               <Chip
                    label={CHIP_LABELS.NOT_APPLICABLE}
                    size={size}
                    color="error"
                    variant={variant}
                    sx={{ ...styles.taskChip, ...sx }}
               />
          );
     }

     // Priority 3: Show regular chip type
     if (chipType) {
          return (
               <Chip
                    label={chipType.label}
                    size={size}
                    color={chipType.color}
                    variant={variant}
                    sx={{
                         ...styles.taskChip,
                         ...(chipType.customColor && {
                              backgroundColor: chipType.customColor,
                              color: 'white',
                              '&:hover': { backgroundColor: chipType.customColor },
                         }),
                         ...sx,
                    }}
               />
          );
     }

     return null;
};